import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type { OCRConfigDTO, OCRConfigUpdateParams } from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid ocr config': 'OCR 配置无效，请检查当前启用的识别引擎配置是否完整',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

type OCRForm = OCRConfigUpdateParams;

const EMPTY_FORM: OCRForm = {
  normal_task_ocr_claim_workers: 1,
  task_priority: 'equal',
  enabled: false,
  provider: 'tencent',
  tencent: {
    secret_id: '',
    secret_key: '',
    region: 'ap-guangzhou',
    endpoint: 'ocr.tencentcloudapi.com',
    mode: 'accurate',
  },
  paddleocr: {
    base_url: 'http://127.0.0.1:19090',
    model_tier: 'medium',
    max_concurrency: 2,
    cpu_threads: 3,
  },
};

function clampConcurrency(value: number | undefined): number {
  if (!Number.isFinite(value)) return 2;
  return Math.min(8, Math.max(1, Math.trunc(value || 2)));
}

function dtoToForm(dto: OCRConfigDTO): OCRForm {
  return {
    normal_task_ocr_claim_workers: dto.normal_task_ocr_claim_workers ?? 1,
    task_priority: dto.task_priority || 'equal',
    enabled: dto.enabled,
    provider: dto.provider,
    tencent: {
      secret_id: dto.tencent.secret_id,
      secret_key: dto.tencent.secret_key,
      region: dto.tencent.region,
      endpoint: dto.tencent.endpoint,
      mode: dto.tencent.mode,
    },
    paddleocr: {
      base_url: dto.paddleocr?.base_url || 'http://127.0.0.1:19090',
      model_tier: dto.paddleocr?.model_tier || 'medium',
      max_concurrency: clampConcurrency(dto.paddleocr?.max_concurrency),
      cpu_threads: dto.paddleocr?.cpu_threads || 3,
    },
  };
}

export function useOCRConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<OCRConfigDTO | null>(null);
  const [form, setForm] = useState<OCRForm>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getOCRConfig();
      setData(result);
      setForm(dtoToForm(result));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setEnabled = useCallback((enabled: boolean) => {
    setForm((prev) => ({ ...prev, enabled }));
  }, []);

  const setProvider = useCallback((provider: OCRForm['provider']) => {
    setForm((prev) => ({ ...prev, provider }));
  }, []);

  const setClaimWorkers = useCallback((workers: number) => {
    setForm((prev) => ({ ...prev, normal_task_ocr_claim_workers: workers }));
  }, []);

  const setTaskPriority = useCallback((priority: OCRForm['task_priority']) => {
    setForm((prev) => ({ ...prev, task_priority: priority }));
  }, []);

  const updateTencentField = useCallback(
    <K extends keyof OCRForm['tencent']>(key: K, value: OCRForm['tencent'][K]) => {
      setForm((prev) => ({ ...prev, tencent: { ...prev.tencent, [key]: value } }));
    },
    [],
  );

  const updatePaddleField = useCallback(
    <K extends keyof OCRForm['paddleocr']>(key: K, value: OCRForm['paddleocr'][K]) => {
      setForm((prev) => ({ ...prev, paddleocr: { ...prev.paddleocr, [key]: value } }));
    },
    [],
  );

  const save = useCallback(async () => {
    if (!Number.isInteger(form.normal_task_ocr_claim_workers) || form.normal_task_ocr_claim_workers < 1 || form.normal_task_ocr_claim_workers > 8) {
      addToast('error', '识别任务总并发数必须为 1～8 的整数');
      return;
    }
    if (!Number.isInteger(form.paddleocr.cpu_threads) || form.paddleocr.cpu_threads < 1 || form.paddleocr.cpu_threads > 32) {
      addToast('error', 'CPU 线程数必须为 1～32 的整数');
      return;
    }
    setSaving(true);
    try {
      const result = await settingsService.updateOCRConfig({
        ...form,
        paddleocr: {
          ...form.paddleocr,
          max_concurrency: clampConcurrency(form.paddleocr.max_concurrency),
        },
      });
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', 'OCR 配置已保存；并发与优先级约 5 秒内同步，识别参数对后续请求生效');
    } catch (e) {
      addToast('error', resolveError(e));
    } finally {
      setSaving(false);
    }
  }, [addToast, form]);

  return {
    data,
    form,
    loading,
    saving,
    error,
    setEnabled,
    setProvider,
    setClaimWorkers,
    setTaskPriority,
    updateTencentField,
    updatePaddleField,
    save,
    reload: load,
  };
}
